import { ChannelEventConstant, ChannelOperationType } from '../constants/event-constants';
import { INITIAL_SEQUENCE_NUMBER, RECONNECTION_TIMEOUT, } from '../constants/value-constants';
import Publisher from '../publisher/publisher';
import Subscriber from '../subscriber/subscriber';
import fetchHistory from '../utils/fetch-pubnub-history';
import { processChannelMessages } from '../utils/process-messages';
import { ChannelStatus } from './channel-types';
const PubnubConnectedCategories = [
    'PNNetworkUpCategory',
    'PNReconnectedCategory',
    'PNConnectedCategory',
];
const PubnubDisconncectedCategories = [
    'PNNetworkDownCategory',
    'PNTimeoutCategory',
    'PNAccessDeniedCategory',
    'PNUnknownCategory',
    'PNRequestMessageCountExceedCategory',
];
class Channel {
    channelOptions;
    pubnubInstance;
    timePromise;
    channelStatus = ChannelStatus.CONNECTING;
    channelMetadata = null;
    publisher;
    subscriber;
    consumerFns = [];
    metadataChangeFns = [];
    statusChangeFns = [];
    reconnectionTimer;
    logger;
    // TODO:Review add getter rather than public member
    messages = [];
    isListnerAttached = false;
    hasSubsriber = false;
    hasPublisher = false;
    constructor(options, pubnub) {
        this.channelOptions = options;
        this.pubnubInstance = pubnub;
        this.logger = options.logger;
        this.timePromise = this.pubnubInstance.time();
        this.addConsumer(options.consumerFn);
        this.addMetadataChangeListner(options.onMetadataChangeFn);
        this.addStatusChangeListner(options.onStatusChangeFn);
        options.onMetadataChangeFn && this.fetchChannelMetadata();
    }
    clearReconnectionTimer = () => {
        if (this.reconnectionTimer) {
            clearTimeout(this.reconnectionTimer);
            this.reconnectionTimer = null;
        }
    };
    triggerReconnectionTimer = () => {
        this.reconnectionTimer = setTimeout(() => {
            this.logger(ChannelEventConstant.CHANNEL_RECONNECTION_TIMEOUT, {});
            this.channelStatus = ChannelStatus.FAILED;
            this.onStatusChange();
        }, RECONNECTION_TIMEOUT);
    };
    statusAndObjectsListner = {
        status: event => {
            const { category, operation, affectedChannels, statusCode } = event;
            this.logger(ChannelEventConstant.CHANNEL_GOT_STATUS_EVENT, {
                category,
                operation,
                affected_channels: affectedChannels,
                status_code: statusCode,
            });
            if (PubnubDisconncectedCategories.indexOf(category) !== -1) {
                this.logger(ChannelEventConstant.CHANNEL_STATUS_RECONNECTING, {
                    category,
                    operation,
                });
                this.channelStatus = ChannelStatus.RECONNECTING;
                this.triggerReconnectionTimer();
            }
            if (PubnubConnectedCategories.indexOf(category) !== -1 &&
                this.channelStatus !== ChannelStatus.CONNECTED) {
                this.clearReconnectionTimer();
                this.logger(ChannelEventConstant.CHANNEL_STATUS_RECONNECTED, {
                    category,
                    previousState: this.channelStatus,
                });
                this.channelStatus = ChannelStatus.CONNECTED;
                if (this.publisher) {
                    this.publisher.attemptPublish();
                }
                this.subscriber?.initAfterReconnect();
            }
            this.onStatusChange();
        },
        objects: (event) => {
            const { message, channel } = event;
            if (message.type === 'channel' && channel === this.channelOptions.channelId) {
                if (message.event === 'set') {
                    const { custom } = message.data;
                    this.channelMetadata = custom?.metadata
                        ? JSON.parse(custom.metadata)
                        : undefined;
                }
                else if (message.event === 'delete') {
                    this.channelMetadata = undefined;
                }
                this.onMetadataChange();
            }
        },
    };
    attachListeners() {
        this.pubnubInstance.addListener(this.statusAndObjectsListner);
        this.isListnerAttached = true;
    }
    async fetchAndProcessBulkMessages(startTimtToken, endTimeToken) {
        try {
            this.logger(ChannelEventConstant.CHANNEL_FETCH_STARTED, {});
            const messageList = await fetchHistory(this.channelOptions.channelId, this.pubnubInstance, startTimtToken, endTimeToken);
            this.logger(ChannelEventConstant.CHANNEL_FETCH_SUCCESS, {
                messagesCount: messageList.length,
            });
            return processChannelMessages(messageList);
        }
        catch (e) {
            this.logger(ChannelEventConstant.CHANNEL_FETCH_FAILED, {
                error: JSON.stringify(e),
            });
            throw new Error(JSON.stringify(e));
        }
    }
    getSubscriberParams() {
        const getAllUuid = [];
        const lastMessagePerUser = {};
        this.messages.forEach(msg => {
            if (msg.uuid &&
                msg.uuid !== this.channelOptions.userId &&
                !getAllUuid.includes(msg.uuid)) {
                getAllUuid.push(msg.uuid);
            }
        });
        getAllUuid.forEach((uuid) => {
            lastMessagePerUser[uuid] = this.getLastSequenceMessage(uuid);
        });
        return {
            channelId: this.channelOptions.channelId,
            lastMessagePerUser,
            pubnubInstance: this.pubnubInstance,
            onReceiveFailed: this.onReceiveFailed,
            onReceiveSucces: this.onReceiveSuccess,
            userId: this.channelOptions.userId,
            noHistory: this.channelOptions.noHistory,
            logger: this.logger,
            ttl: this.channelOptions.ttl,
        };
    }
    getLastSequenceMessage(uuid) {
        let lastMessage = null;
        let sequence = -1;
        this.messages.forEach(msg => {
            if (msg.uuid === uuid && msg.message.sequence > sequence) {
                lastMessage = msg;
                sequence = msg.message.sequence;
            }
        });
        return lastMessage;
    }
    getPublisherParams = async () => {
        const lastMessage = this.channelOptions.userId
            ? this.getLastSequenceMessage(this.channelOptions.userId)
            : null;
        let sequenceStartTime = 0;
        const initialSequence = lastMessage
            ? lastMessage.message?.sequence
            : INITIAL_SEQUENCE_NUMBER;
        if (this.channelOptions.noHistory) {
            try {
                const { timetoken } = await this.timePromise;
                // sequence start time is in milliseconds
                sequenceStartTime = Math.floor(timetoken / 10000);
            }
            catch (error) {
                this.logger(ChannelEventConstant.CHANNEL_TIMETOKEN_FETCH_FAILED, {
                    ...(error ?? {}),
                    errorString: JSON.stringify(error),
                });
                sequenceStartTime = new Date().getTime();
            }
        }
        return {
            channelId: this.channelOptions.channelId,
            ttl: this.channelOptions.ttl,
            initialSequence,
            sequenceStartTime,
            pubnub: this.pubnubInstance,
            onPublishSucces: this.onPublishSuccess,
            onPublishFailed: this.onPubishFailed,
            logger: this.logger,
            getChannelStatus: this.getChannelStatus,
        };
    };
    // channel utils for unit testing
    getChannelStatus = () => {
        return this.channelStatus;
    };
    fetchChannelMetadata = async () => {
        try {
            const response = await this.pubnubInstance.objects.getChannelMetadata({
                channel: this.channelOptions.channelId,
            });
            this.channelMetadata = response.data.custom?.metadata
                ? JSON.parse(response.data.custom.metadata)
                : undefined;
        }
        catch {
            this.channelMetadata = undefined;
            // When channel metadata is not set or removed
        }
        this.onMetadataChange();
    };
    /**
     * @param metadata metadata to be set for the channel
     * @param merge if true, merge the metadata with existing metadata, default is true
     */
    setChannelMetadata = (metadata, merge = true) => {
        this.pubnubInstance.objects.setChannelMetadata({
            channel: this.channelOptions.channelId,
            data: {
                custom: {
                    metadata: JSON.stringify(merge ? { ...this.channelMetadata, ...metadata } : metadata),
                },
            },
        });
    };
    removeChannelMetadata = () => {
        this.pubnubInstance.objects.removeChannelMetadata({
            channel: this.channelOptions.channelId,
        });
    };
    getChannelMetadata = () => {
        return this.channelMetadata;
    };
    addConsumer(consumerFn) {
        if (consumerFn && !this.consumerFns.includes(consumerFn))
            this.consumerFns.push(consumerFn);
    }
    removeConsumer(consumerFn) {
        this.consumerFns = this.consumerFns.filter(fn => fn !== consumerFn);
    }
    clearConsumers() {
        this.consumerFns = [];
        this.metadataChangeFns = [];
        this.statusChangeFns = [];
    }
    addStatusChangeListner(statusChangeFn) {
        if (statusChangeFn && !this.statusChangeFns.includes(statusChangeFn))
            this.statusChangeFns.push(statusChangeFn);
    }
    removeStatusChangeListner(statusChangeFn) {
        this.statusChangeFns = this.statusChangeFns.filter(fn => fn !== statusChangeFn);
    }
    addMetadataChangeListner(metadataChangeFn) {
        if (metadataChangeFn && !this.metadataChangeFns.includes(metadataChangeFn))
            this.metadataChangeFns.push(metadataChangeFn);
    }
    removeMetadataChangeListner(metadataChangeFn) {
        this.metadataChangeFns = this.metadataChangeFns.filter(fn => fn !== metadataChangeFn);
    }
    initExistingChannel(consumerFn) {
        this.consumeInitialData(this.messages, consumerFn);
    }
    initSubscriber() {
        const subscriberParams = this.getSubscriberParams();
        this.subscriber = new Subscriber(subscriberParams);
        this.subscriber.init();
        this.hasSubsriber = true;
    }
    async initPublisher() {
        const publisherParams = await this.getPublisherParams();
        this.publisher = new Publisher(publisherParams);
        this.hasPublisher = true;
    }
    consumeInitialData(messages, consumerFn) {
        if (consumerFn) {
            consumerFn(messages, true);
        }
        else {
            this.consumerFns.forEach(consumeFn => {
                consumeFn(messages, true);
            });
        }
    }
    /**
     * attachListeners on pubnub status for the channel
     * calls fetch hisotry
     * create publisher - last message
     * create subscriber - last message of each user
     * returns current instance
     * error: throws a error if any of the tasks are failed
     */
    async init() {
        try {
            this.attachListeners();
            if (this.channelOptions.noHistory) {
                this.initSubscriber();
                await this.initPublisher();
            }
            else {
                const messages = await this.fetchAndProcessBulkMessages();
                // To fix the sequence number for non noHistory case
                if (!this.channelOptions.noHistory) {
                    messages.forEach(msgObject => {
                        msgObject.message.sequenceStartTime = 0;
                    });
                }
                this.messages = messages;
                this.consumeInitialData(messages);
                this.initSubscriber();
                await this.initPublisher();
            }
            this.channelStatus = ChannelStatus.CONNECTED;
            this.logger(ChannelEventConstant.CHANNEL_STATUS_CONNECTED, {});
            return this;
        }
        catch (error) {
            // TODO:ERROR:CHANNEL
            const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
            this.channelStatus = ChannelStatus.INIT_FAILED;
            this.logger(ChannelEventConstant.CHANNEL_INIT_FAILED, { error: errorMessage });
            throw new Error(errorMessage);
        }
    }
    publish = (message) => {
        if (this.publisher &&
            (this.channelStatus === ChannelStatus.CONNECTED ||
                this.channelStatus === ChannelStatus.RECONNECTING)) {
            this.publisher.publish(message);
            return true;
        }
        this.logger(ChannelEventConstant.CHANNEL_PUBLISH_CALLED_BEFORE_INIT, {
            status: this.channelStatus,
        });
        return false;
    };
    // on publish success update master list
    onPublishSuccess = (message) => {
        this.messages.push({
            message: message,
            channel: this.channelOptions.channelId,
            uuid: this.channelOptions.userId || '',
        });
    };
    onPubishFailed = () => {
        this.channelStatus = ChannelStatus.FAILED;
        this.logger(ChannelEventConstant.CHANNEL_STATUS_FAILED, {
            operation: ChannelOperationType.PUBLISHER,
        });
    };
    onReceiveSuccess = (messages) => {
        messages.forEach(msgObject => {
            this.messages.push(msgObject);
        });
        this.consumerFns.forEach(consumerFn => consumerFn(messages));
    };
    onReceiveFailed() {
        this.channelStatus = ChannelStatus.FAILED;
        this.logger(ChannelEventConstant.CHANNEL_STATUS_FAILED, {
            category: ChannelOperationType.SUBSCRIBER,
        });
    }
    onStatusChange() {
        this.statusChangeFns.forEach(statusChangeFn => {
            statusChangeFn(this.channelStatus, this.publisher?.publisherQueue || []);
        });
    }
    onMetadataChange() {
        this.metadataChangeFns.forEach(metadataChangeFn => {
            metadataChangeFn(this.channelMetadata);
        });
    }
    destroyChannel() {
        this.clearConsumers();
        this.pubnubInstance.removeListener(this.statusAndObjectsListner);
        this.subscriber?.destroySubscriber();
    }
}
export default Channel;
//# sourceMappingURL=channel.js.map